import { User } from '../user';

export enum AutoGradedRubricGenerationStatus {
	COMPLETE = 'complete',
	FAILED = 'failed',
	NONE = 'none',
	NOT_APPLICABLE = 'not_applicable',
	PROCESSING = 'processing',
	QUEUED = 'queued'
}

export interface AutoGradedRubricGenerationState {
	attempt_number?: number | null;
	can_retry?: boolean;
	remaining_retries?: number | null;
	rubric_session_id?: number | null;
	status: AutoGradedRubricGenerationStatus;
}

export enum RubricEvaluatorOptionType {
	GO_REACT_AI = 'go_react_ai',
	HUMAN = 'human'
}

export interface HumanRubricEvaluatorOption {
	id: string;
	rubric: RubricSession;
	type: RubricEvaluatorOptionType.HUMAN;
	user: User;
}

export interface GoReactAiRubricEvaluatorOption {
	generationState: AutoGradedRubricGenerationState;
	id: 'go-react-ai';
	rubric: RubricSession | null;
	type: RubricEvaluatorOptionType.GO_REACT_AI;
	user: User;
}

export type RubricEvaluatorOption = HumanRubricEvaluatorOption | GoReactAiRubricEvaluatorOption;

export interface RubricSession {
	id: number;
	points: number;
	can_override_score?: boolean;
	created_by?: number;
	has_viewed?: boolean;
	hideScore?: boolean;
	is_ai_generated?: boolean;
	original_score?: ScoreOriginal | null;
	role: string;
	session_id: number;
	session_score?: number | null;
	session_graded_at?: string | null;
	session_is_graded?: boolean;
	template_id: number;
	data?: RubricSessionElement[];
	title: string;
	total: number;
	user: User;
	isPublished?: () => boolean;
}

export interface ScoreOriginal {
	points: string | null;
	value: string | null;
	rubric_element_option_id: number | null;
}

export interface RubricSessionElement {
	id: number;
	type: string;
	label?: string;
	points?: number | string | null;
	value?: number | string | null;
	option?: number | null;
	options?: RubricTemplateElementOption[];
	can_override_score?: boolean;
	original_score?: ScoreOriginal | null;
	justification?: RubricSessionElementJustification | null;
}

export interface RubricSessionElementJustification {
	summary: string;
	instructor_comment: string | null;
	evidence?: RubricSessionElementJustificationEvidence[];
}

export interface RubricSessionElementJustificationEvidence {
	timestamp_ms: number;
	description: string;
}

export interface RubricScoreOverrideElement {
	id: number;
	points?: number | string | null;
	value?: boolean;
	option?: number | null;
	instructor_comment?: string | null;
}

export interface RubricScoreOverrideRequest {
	points?: number | null;
	elements?: RubricScoreOverrideElement[];
}

export interface RubricScoreOverrideResponse {
	rubric: RubricSession;
	session: {
		score: number | null;
		graded_at: string | null;
		is_graded: boolean;
	};
}

export interface RubricTemplate {
	id: number;
	title: string;
	desc: string | null;
	total: number | string;
	elements: RubricTemplateElement[];
	created_at: string;
	created_by: number;
	modified_at: string | null;
	deleted_at: string | null;
	deleted_by: number | null;
	usage: number;
	parent_id: number | null;
}

export interface RubricTemplateElementOption {
	id: number;
	label: string;
	desc: string;
	value: number | string
}

interface RubricTemplateElement {
	id: number;
	type: string;
	title: string;
	label?: string;
	desc: string;
	total?: number | null;
	value?: number | string | null;
	options?: RubricTemplateElementOption[]
}

export interface RubricPreviewTemplateElement
	extends Partial<Omit<RubricTemplateElement, 'id' | 'type' | 'total'>> {
	id: RubricTemplateElement['id'];
	type: RubricTemplateElement['type'];
	total?: number | string | null;
}
